import { recordError, recordEvent } from './observability';

export type ShareOrCopyOutcome = 'shared' | 'copied' | 'cancelled';

type ShareOrCopyParams = {
  url: string;
  title?: string;
  text?: string;
  listTitle?: string;
};

const getErrorName = (error: unknown): string => {
  if (error && typeof error === 'object' && 'name' in error) {
    return String((error as { name?: string }).name || '');
  }

  return '';
};

export const shareOrCopyLink = async ({
  url,
  title,
  text,
  listTitle,
}: ShareOrCopyParams): Promise<ShareOrCopyOutcome> => {
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({
        title,
        text,
        url,
      });

      recordEvent('list_shared', {
        listTitle,
        method: 'native_share',
        outcome: 'shared',
      });

      return 'shared';
    } catch (error) {
      if (getErrorName(error) === 'AbortError') {
        recordEvent('list_shared', {
          listTitle,
          method: 'native_share',
          outcome: 'cancelled',
        });
        return 'cancelled';
      }

      recordError(error instanceof Error ? error : new Error(String(error)), {
        action: 'share_link',
        listTitle,
      });
    }
  }

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(url);

      recordEvent('list_shared', {
        listTitle,
        method: 'clipboard',
        outcome: 'copied',
      });

      return 'copied';
    } catch (error) {
      recordError(error instanceof Error ? error : new Error(String(error)), {
        action: 'copy_link',
        listTitle,
      });
    }
  }

  const error = new Error('No available sharing strategy for this browser.');
  recordError(error, { action: 'share_link', listTitle });
  throw error;
};

export const copyLinkToClipboard = async (url: string): Promise<void> => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(url);
    return;
  }

  throw new Error('Clipboard API is not available in this browser.');
};
